import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../../utils/api';
import './CharacterAssignmentPopover.css';

// Button + popover for viewing (everyone) and changing (staff only) which of a
// player's characters is Primary/Secondary. The button itself always shows
// the *currently open* character's status; the popover panel lists every
// character belonging to that character's player.
const CharacterAssignmentPopover = ({
    token,
    playerId,
    currentCharacterId,
    currentCharacterStatus,
    staffPermissions,
    onAssignmentChanged,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [characters, setCharacters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const containerRef = useRef(null);


    const loadCharacters = async () => {
        if (playerId == null) return;
        setLoading(true);
        try {
            const list = await api.fetchCharactersByPlayer(playerId);
            setCharacters(Array.isArray(list) ? list : []);
        } catch {
            toast.error("Failed to load this player's characters.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) loadCharacters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, playerId]);

    // Click-outside to close.
    useEffect(() => {
        if (!isOpen) return;
        const onDocClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [isOpen]);

    const applyAssignment = async (nextPrimaryId, nextSecondaryId) => {
        setSaving(true);
        try {
            await api.updateCharacterAssignments(playerId, {
                primaryCharacterId: nextPrimaryId,
                secondaryCharacterId: nextSecondaryId,
            });
            await loadCharacters();
            onAssignmentChanged?.();
        } catch {
            toast.error('Failed to update character assignment.');
        } finally {
            setSaving(false);
        }
    };

    const currentPrimaryId = characters.find((c) => c.characterStatus === 'Primary')?.characterId ?? null;
    const currentSecondaryId = characters.find((c) => c.characterStatus === 'Secondary')?.characterId ?? null;

    // Swap semantics (mirrors the skillset-swap UX in CharacterForm): picking
    // a character for a slot it doesn't already hold moves whoever previously
    // held that slot into the slot being vacated, rather than leaving gaps.
    const makePrimary = (characterId) => {
        if (saving || characterId === currentPrimaryId) return;
        const nextSecondaryId = characterId === currentSecondaryId ? currentPrimaryId : currentSecondaryId;
        applyAssignment(characterId, nextSecondaryId);
    };

    const makeSecondary = (characterId) => {
        if (saving || characterId === currentSecondaryId) return;
        const nextPrimaryId = characterId === currentPrimaryId ? currentSecondaryId : currentPrimaryId;
        applyAssignment(nextPrimaryId, characterId);
    };

    return (
        <div className="assignment-popover-container" ref={containerRef}>
            <button
                type="button"
                className="print-btn"
                onClick={() => setIsOpen((o) => !o)}
                title="View/change Primary, Secondary, and Tertiary characters"
            >
                {currentCharacterStatus || 'Tertiary'}
            </button>

            {isOpen && (
                <div className="assignment-popover-panel" role="dialog" aria-label="Primary/Secondary character assignment">
                    <div className="assignment-popover-title">Character Assignments</div>
                    {loading ? (
                        <div className="assignment-popover-empty">Loading…</div>
                    ) : characters.length === 0 ? (
                        <div className="assignment-popover-empty">No characters found.</div>
                    ) : (
                        <ul className="assignment-popover-list">
                            {characters.map((c) => (
                                <li
                                    key={c.characterId}
                                    className={`assignment-popover-row${c.characterId === currentCharacterId ? ' is-current' : ''}`}
                                >
                                    <span className="assignment-popover-name" title={c.name}>{c.name}</span>
                                    <span className="assignment-popover-actions">
                                        <button
                                            type="button"
                                            className={`assignment-popover-role-btn${c.characterStatus === 'Primary' ? ' is-active' : ''}`}
                                            disabled={!staffPermissions || saving || c.characterStatus === 'Primary'}
                                            onClick={() => makePrimary(c.characterId)}
                                        >
                                            Primary
                                        </button>
                                        <button
                                            type="button"
                                            className={`assignment-popover-role-btn${c.characterStatus === 'Secondary' ? ' is-active' : ''}`}
                                            disabled={!staffPermissions || saving || c.characterStatus === 'Secondary'}
                                            onClick={() => makeSecondary(c.characterId)}
                                        >
                                            Secondary
                                        </button>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {!staffPermissions && (
                        <div className="assignment-popover-hint">Only staff can change these assignments.</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CharacterAssignmentPopover;
